/**
 * Widget J: Location Insights (By State/Province)
 * 
 * Displays a vertical bar chart showing client count by state/province,
 * with tooltips showing average income and average readiness ratio.
 */

import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { ClientType } from "../../../types";
import { calculateLocationInsights, LocationInsightsData } from "../utils/widgetCalculations";
import { UNIFIED_CHART_COLORS, ACCENT_COLOR, getUnifiedChartColor } from "./widgetColors";
import { ChartTooltip, BAR_CHART_MARGINS, AXIS_STYLE, GRID_STYLE, CHART_ANIMATION } from "./chartUtils";
import XanoAPI from "../../../lib/XanoAPI";

interface LocationInsightsWidgetProps {
  clients: ClientType[];
  isLoading?: boolean;
  userId?: number; // Optional user ID filter (singular int)
}

const formatCurrency = (value: number): string => {
  if (value >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(2)}M`;
  }
  if (value >= 1_000) {
    return `$${(value / 1_000).toFixed(2)}K`;
  }
  return `$${value.toFixed(2)}`;
};

export function LocationInsightsWidget({
  clients,
  isLoading = false,
  userId,
}: LocationInsightsWidgetProps) {
  const api = XanoAPI.getInstance();

  // Fetch data from API endpoint
  const { data: apiData, isLoading: apiLoading } = useQuery({
    queryKey: ["location-insights", userId],
    queryFn: async () => {
      const result = await api.getLocationInsights(userId);
      console.log("🔍 LocationInsightsWidget: API response:", result);
      console.log("🔍 LocationInsightsWidget: API response type:", typeof result);
      console.log("🔍 LocationInsightsWidget: Has locations?", !!result?.locations);
      console.log("🔍 LocationInsightsWidget: Locations count:", result?.locations?.length);
      if (!result) {
        console.log("⚠️ LocationInsightsWidget: Endpoint returned null");
      }
      return result;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });

  // State abbreviation to full name mapping
  const stateAbbrToFullName: Record<string, string> = {
    "AL": "Alabama", "AK": "Alaska", "AZ": "Arizona", "AR": "Arkansas", "CA": "California",
    "CO": "Colorado", "CT": "Connecticut", "DE": "Delaware", "FL": "Florida", "GA": "Georgia",
    "HI": "Hawaii", "ID": "Idaho", "IL": "Illinois", "IN": "Indiana", "IA": "Iowa",
    "KS": "Kansas", "KY": "Kentucky", "LA": "Louisiana", "ME": "Maine", "MD": "Maryland",
    "MA": "Massachusetts", "MI": "Michigan", "MN": "Minnesota", "MS": "Mississippi", "MO": "Missouri",
    "MT": "Montana", "NE": "Nebraska", "NV": "Nevada", "NH": "New Hampshire", "NJ": "New Jersey",
    "NM": "New Mexico", "NY": "New York", "NC": "North Carolina", "ND": "North Dakota", "OH": "Ohio",
    "OK": "Oklahoma", "OR": "Oregon", "PA": "Pennsylvania", "RI": "Rhode Island", "SC": "South Carolina",
    "SD": "South Dakota", "TN": "Tennessee", "TX": "Texas", "UT": "Utah", "VT": "Vermont",
    "VA": "Virginia", "WA": "Washington", "WV": "West Virginia", "WI": "Wisconsin", "WY": "Wyoming",
    "DC": "District of Columbia"
  };

  const data = useMemo(() => {
    let result: LocationInsightsData;
    
    if (apiData && apiData.locations && Array.isArray(apiData.locations) && apiData.locations.length > 0) {
      // API data is available - transform it to match expected format
      // API uses 'name' field for state abbreviations (e.g., "CA"), we need to convert to full names
      result = {
        locations: apiData.locations.map((loc: any) => {
          // Get state value from name or state field
          const stateValue = (loc.name || loc.state || "").trim();
          
          // Convert abbreviation to full name if needed
          let fullStateName: string;
          if (stateValue.length === 2) {
            fullStateName = stateAbbrToFullName[stateValue.toUpperCase()] || stateValue;
          } else {
            fullStateName = stateValue;
          }
          
          return {
            state: fullStateName,
            count: loc.count || 0,
            avgIncome: loc.avgIncome || 0,
            avgReadiness: loc.avgReadiness || 0,
          };
        })
      };
    } else {
      // No API data - calculate from clients as fallback
      result = calculateLocationInsights(clients);
    }
    
    // Sort by count descending - show all states, not just top 10
    const sortedLocations = result.locations
      .sort((a, b) => b.count - a.count)
      .filter(loc => loc.count > 0); // Only show states with clients
    
    // Transform to chart data format
    const chartData = sortedLocations.map((location, index) => ({
      state: location.state,
      count: location.count,
      avgIncome: location.avgIncome,
      avgReadiness: location.avgReadiness,
      fill: getUnifiedChartColor(index % UNIFIED_CHART_COLORS.length),
    }));

    return { 
      chartData, 
      totalLocations: sortedLocations.length, 
      totalClients: sortedLocations.reduce((sum, d) => sum + d.count, 0)
    };
  }, [apiData, clients]);

  const isLoadingData = isLoading || apiLoading;

  if (isLoadingData) {
    return (
      <Card>
        <CardContent>
          <div className="h-[340px] flex items-center justify-center">
            <p className="text-muted-foreground text-sm">Loading...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (data.chartData.length === 0) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Location Insights</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[340px] flex items-center justify-center">
            <p className="text-muted-foreground">
              No location data available for the selected clients.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }


  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">Users Per State</CardTitle>
        <span className="text-xs text-muted-foreground ml-2">
          {data.totalClients} clients across {data.totalLocations} states
        </span>
      </CardHeader>
      <CardContent>
        <div className="h-[340px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.chartData} margin={BAR_CHART_MARGINS}>
              <CartesianGrid {...GRID_STYLE} vertical={false} />
              <XAxis 
                dataKey="state" 
                angle={data.chartData.length > 10 ? -45 : 0}
                textAnchor={data.chartData.length > 10 ? "end" : "middle"}
                height={data.chartData.length > 10 ? 100 : 30}
                interval={data.chartData.length > 20 ? Math.floor(data.chartData.length / 20) : 0}
                tick={AXIS_STYLE.tick}
                tickLine={false}
              />
              <YAxis 
                label={{ 
                  value: "Number of Clients", 
                  angle: -90, 
                  position: "insideLeft",
                  style: AXIS_STYLE.label
                }}
                allowDecimals={false}
                tick={AXIS_STYLE.tick}
                tickLine={false}
                width={25}
              />
              <Tooltip 
                content={<ChartTooltip 
                  formatter={(value, name) => {
                    const dataPoint = data.chartData.find(d => d.count === value);
                    const total = data.chartData.reduce((sum, d) => sum + d.count, 0);
                    const percentage = total > 0
                      ? ((value / total) * 100).toFixed(1)
                      : "0.0";
                    return [
                      `${value} clients (${percentage}%)`,
                      dataPoint?.state || name
                    ];
                  }}
                  labelFormatter={(label) => {
                    const dataPoint = data.chartData.find(d => d.state === label);
                    if (!dataPoint) return label;
                    return `${label} - Income: ${formatCurrency(dataPoint.avgIncome)}, Readiness: ${(dataPoint.avgReadiness * 100).toFixed(1)}%`;
                  }}
                />} 
                cursor={{ fill: "rgba(0, 0, 0, 0.05)" }}
              />
              <Bar 
                dataKey="count" 
                radius={[8, 8, 0, 0]}
                {...CHART_ANIMATION}
              >
                {data.chartData.map((entry, index) => (
                  <Cell key={`location-cell-${entry.state}-${index}`} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        {data.chartData.length <= 15 && (
          <div className="mt-4 flex flex-wrap justify-start gap-x-4 gap-y-3 pt-3 border-t">
            {data.chartData.map((item, index) => {
              const percentage = data.totalClients > 0 ? ((item.count / data.totalClients) * 100).toFixed(1) : "0.0";
              return (
                <div key={`location-${item.state}-${index}`} className="space-y-1 min-w-[100px]">
                  <div className="text-sm font-bold" style={{ color: item.fill }}>
                    {item.state}
                  </div>
                  <div className="text-xs font-medium text-foreground">
                    {item.count} {item.count === 1 ? 'client' : 'clients'} ({percentage}%)
                  </div>
                  {item.avgIncome > 0 && (
                    <div className="text-xs text-muted-foreground">
                      Avg Income: {formatCurrency(item.avgIncome)}
                    </div>
                  )}
                  {item.avgReadiness > 0 && (
                    <div className="text-xs text-muted-foreground">
                      Avg Readiness: {(item.avgReadiness * 100).toFixed(1)}%
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

